import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import type { CategoryWithChildren } from "../../../helpers/productsCategoryHelpers";
import { hasCategoryProducts } from "../../../helpers/productsCategoryHelpers";

interface CategoriesBarLargeProps {
  categories: CategoryWithChildren[];
  activeCategoryIds: number[];
  onCategoryClick: (categoryId: number | null) => void;
  isLoading?: boolean;
}

const CategoriesBarLarge: React.FC<CategoriesBarLargeProps> = ({
  categories,
  activeCategoryIds,
  onCategoryClick,
  isLoading = false,
}) => {
  const { t } = useTranslation("common");
  const [hoveredParentId, setHoveredParentId] = useState<number | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const startCloseTimeout = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredParentId(null);
    }, 200);
  };

  const handleMouseEnterItem = (categoryId: number) => {
    clearCloseTimeout();
    setHoveredParentId(categoryId);
  };

  const handleMouseLeaveItem = () => {
    startCloseTimeout();
  };

  const handleMouseEnterDropdown = () => {
    clearCloseTimeout();
  };

  const handleMouseLeaveDropdown = () => {
    startCloseTimeout();
  };

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    categoryId: number | null
  ) => {
    e.preventDefault();
    onCategoryClick(categoryId);
    setHoveredParentId(null);
    clearCloseTimeout();
  };

  const isAllProductsActive = activeCategoryIds.length === 0;

  return (
    <nav
      className="categories-bar categories-bar--large"
      aria-label={t("productArchive.categories")}
    >
      <ul className="categories-bar__list categories-bar__list--parents">
        <li
          className={`categories-bar__item ${
            isAllProductsActive ? "is-active" : ""
          }`}
          onMouseEnter={() => handleMouseEnterItem(0)}
          onMouseLeave={handleMouseLeaveItem}
        >
          {isLoading ? (
            <Skeleton width={100} height={36} />
          ) : (
            <a
              href="#"
              onClick={(e) => handleClick(e, null)}
              className="categories-bar__link"
              aria-current={isAllProductsActive ? "page" : undefined}
            >
              {t("productArchive.allProducts")}
            </a>
          )}
        </li>

        {isLoading
          ? Array.from({ length: 5 }).map((_, index) => (
              <li key={`skel-${index}`} className="categories-bar__item">
                <Skeleton width={120} height={36} />
              </li>
            ))
          : categories.map((category) => {
              const isActive = activeCategoryIds.includes(category.id);
              const subCategories =
                category.children?.filter(hasCategoryProducts) || [];
              const hasSubcategories = subCategories.length > 0;
              const isHovered = category.id === hoveredParentId;

              return (
                <li
                  key={category.id}
                  className={`categories-bar__item ${
                    isActive ? "is-active" : ""
                  }`}
                  onMouseEnter={() => handleMouseEnterItem(category.id)}
                  onMouseLeave={handleMouseLeaveItem}
                >
                  <a
                    href="#"
                    onClick={(e) => handleClick(e, category.id)}
                    className="categories-bar__link"
                    aria-current={isActive ? "page" : undefined}
                    aria-haspopup={hasSubcategories ? "true" : undefined}
                    aria-expanded={hasSubcategories && isHovered}
                  >
                    {category.name}
                    {hasSubcategories && (
                      <span className="categories-bar__arrow">▼</span>
                    )}
                  </a>

                  {hasSubcategories && isHovered && (
                    <div
                      className="categories-bar__dropdown"
                      onMouseEnter={handleMouseEnterDropdown}
                      onMouseLeave={handleMouseLeaveDropdown}
                    >
                      <ul className="categories-bar__list categories-bar__list--children">
                        {subCategories.map((subCategory) => {
                          const isSubActive = activeCategoryIds.includes(
                            subCategory.id
                          );
                          return (
                            <li
                              key={subCategory.id}
                              className={`categories-bar__item ${
                                isSubActive ? "is-active" : ""
                              }`}
                            >
                              <a
                                href="#"
                                onClick={(e) => handleClick(e, subCategory.id)}
                                className="categories-bar__link"
                                aria-current={isSubActive ? "page" : undefined}
                              >
                                {subCategory.name}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </li>
              );
            })}
      </ul>
    </nav>
  );
};

export default CategoriesBarLarge;
